import { ChangeEvent, Dispatch, useState } from 'react';
import type { DateInputChangeEvent, DateInputValue } from 'nhsuk-react-components';
import {
  Row, Col, DateInput,
} from 'nhsuk-react-components';
import { conditionTargetToLabel } from 'utils/formattingUtils';
import { RuleEditor } from 'schemas/IterationRule.schema';
import { RuleActions } from 'reducers/rule/ruleReducer';
import {
  getRuleEditorControl, getRuleComparatorVariables,
  updateRuleComparator,
  isValidDate,
  hasVaccineTargetControl,
  updateRuleName,
} from 'utils/ruleEditorUtils';
import { ErrorsDisplay } from '../ErrorsDisplay';
import { VaccineTarget } from '../Controls/VaccineTarget';

export interface DateEditorProps {
  ruleEditor?: RuleEditor,
  dispatch: Dispatch<RuleActions>;
  value: string;
  conditionType: string;
  errors: Record<string, string>;
}

export function DateEditor({
  ruleEditor,
  dispatch,
  value,
  conditionType,
  errors,
}: DateEditorProps) {
  const { label: dateLabel } = getRuleEditorControl(ruleEditor, 'DATE');

  const hasVaccineTarget = hasVaccineTargetControl(ruleEditor);

  const [dateString] = getRuleComparatorVariables(value, ruleEditor?.ComparatorTemplate);
  const [dateValue, setDateValue] = useState(() => {
    if (dateString) {
      const year = dateString.slice(0, 4);
      const month = dateString.slice(4, 6);
      const day = dateString.slice(6, 8);
      return {
        day: day || '',
        month: month || '',
        year: year || '',
      };
    }
    return { day: '', month: '', year: '' };
  });

  const handleDateChange = (e: DateInputChangeEvent) => {
    const { day = '', month = '', year = '' } = (e.target.value || {}) as Partial<DateInputValue>;
    const newDateValue = { day, month, year };
    setDateValue(newDateValue);

    const newDateString = `${newDateValue.year}${newDateValue.month.padStart(2, '0')}${newDateValue.day.padStart(2, '0')}`;
    const formattedDateString = `${newDateValue.day.padStart(2, '0')}/${newDateValue.month.padStart(2, '0')}/${newDateValue.year}`;

    const isValid = isValidDate(newDateValue.day, newDateValue.month, newDateValue.year);

    if (isValid) {
      const formattedVars = conditionType && hasVaccineTarget
        ? [conditionTargetToLabel(conditionType), formattedDateString]
        : [formattedDateString];

      updateRuleComparator(dispatch, ruleEditor, newDateString, [newDateString], formattedVars);
      return;
    }

    updateRuleComparator(dispatch, ruleEditor, '', [], []);
  };

  const handleConditionTargetChange = (e: ChangeEvent<HTMLSelectElement>) => {
    dispatch({ type: 'SET_CONDITION_TYPE', payload: e.target.value });
    const formattedDateString = `${dateValue.day.padStart(2, '0')}/${dateValue.month.padStart(2, '0')}/${dateValue.year}`;
    updateRuleName(dispatch, ruleEditor, [conditionTargetToLabel(e.target.value), formattedDateString]);
  };

  return (
    <>
      {hasVaccineTarget && (
        <Row>
          <Col width="full">
            <VaccineTarget
              value={conditionType}
              onChange={handleConditionTargetChange}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col width="full">
          <DateInput
            value={dateValue}
            legend={dateLabel}
            onChange={handleDateChange}
            data-testid="rule-comparator-date"
          />
        </Col>
      </Row>
      <ErrorsDisplay errors={errors} errorKeys={['Comparator', 'AttributeName']} />
    </>
  );
}
